import React, { useState, useEffect } from 'react';
import { useApi } from '../hooks/useApi';
import { Plus, Edit, Trash2, Star, Save, X, User, Building, Image as ImageIcon, Award } from 'lucide-react';

const TestimonialsManager = () => {
  const api = useApi();
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);

  // Form state for creating/editing testimonials
  const [formData, setFormData] = useState({
    client_name: '',
    position: '',
    organization: '',
    content: '',
    rating: 5,
    client_image_url: '',
    is_featured: false,
    order: 0
  });

  useEffect(() => {
    loadTestimonials();
  }, []);

  const loadTestimonials = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/testimonials');
      if (response.success) {
        setTestimonials(response.data);
      }
    } catch (error) {
      console.error('Error loading testimonials:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTestimonial = async () => {
    try {
      const testimonialData = {
        ...formData,
        order: testimonials.length + 1
      };

      const response = await api.post('/admin/testimonials', testimonialData);
      if (response.success) {
        loadTestimonials();
        resetForm();
        setShowCreateForm(false);
      }
    } catch (error) {
      console.error('Error creating testimonial:', error);
    }
  };

  const handleUpdateTestimonial = async () => {
    try {
      const response = await api.put(`/admin/testimonials/${editingTestimonial}`, formData);
      if (response.success) {
        loadTestimonials();
        resetForm();
        setEditingTestimonial(null);
      }
    } catch (error) {
      console.error('Error updating testimonial:', error);
    }
  };

  const handleDeleteTestimonial = async (testimonialId) => {
    if (window.confirm('Are you sure you want to delete this testimonial?')) {
      try {
        const response = await api.delete(`/admin/testimonials/${testimonialId}`);
        if (response.success) {
          loadTestimonials();
        }
      } catch (error) {
        console.error('Error deleting testimonial:', error);
      }
    }
  };

  const handleToggleFeatured = async (testimonialId) => {
    try {
      const response = await api.put(`/admin/testimonials/${testimonialId}/feature`);
      if (response.success) {
        loadTestimonials();
      }
    } catch (error) {
      console.error('Error toggling featured status:', error);
    }
  };

  const startEdit = (testimonial) => {
    setFormData({
      client_name: testimonial.client_name,
      position: testimonial.position,
      organization: testimonial.organization,
      content: testimonial.content,
      rating: testimonial.rating,
      client_image_url: testimonial.client_image_url || '',
      is_featured: testimonial.is_featured,
      order: testimonial.order
    });
    setEditingTestimonial(testimonial.id);
    setShowCreateForm(false);
  };

  const resetForm = () => {
    setFormData({
      client_name: '',
      position: '',
      organization: '',
      content: '',
      rating: 5,
      client_image_url: '',
      is_featured: false,
      order: 0
    });
  };

  const StarRating = ({ rating, onChange, readonly = false }) => {
    return (
      <div className="flex items-center space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => !readonly && onChange && onChange(star)}
            className={`${readonly ? 'cursor-default' : 'cursor-pointer hover:scale-110'} transition-transform`}
            disabled={readonly}
          >
            <Star
              size={16}
              className={`${
                star <= rating
                  ? 'text-yellow-400 fill-current'
                  : 'text-gray-300'
              }`}
            />
          </button>
        ))}
        {!readonly && (
          <span className="text-sm text-gray-500 ml-2">({rating}/5)</span>
        )}
      </div>
    );
  };

  const TestimonialCard = ({ testimonial }) => (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow relative">
      {testimonial.is_featured && (
        <div className="absolute top-4 right-4">
          <div className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs font-medium flex items-center space-x-1">
            <Award size={12} />
            <span>Featured</span>
          </div>
        </div>
      )}
      
      <div className="flex items-start space-x-4 mb-4">
        {testimonial.client_image_url ? (
          <img 
            src={testimonial.client_image_url} 
            alt={testimonial.client_name}
            className="w-12 h-12 rounded-full object-cover"
          />
        ) : (
          <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
            <User className="w-6 h-6 text-blue-600" />
          </div>
        )}
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900">{testimonial.client_name}</h3>
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <span>{testimonial.position}</span>
            <span>•</span>
            <div className="flex items-center space-x-1">
              <Building size={14} />
              <span>{testimonial.organization}</span>
            </div>
          </div>
          <div className="mt-2">
            <StarRating rating={testimonial.rating} readonly />
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => handleToggleFeatured(testimonial.id)}
            className={`p-2 rounded-lg ${testimonial.is_featured ? 'text-yellow-600 hover:bg-yellow-50' : 'text-gray-400 hover:bg-gray-50'}`}
            title={testimonial.is_featured ? 'Featured - Click to unfeature' : 'Not featured - Click to feature'}
          >
            <Award size={18} />
          </button>
          <button
            onClick={() => startEdit(testimonial)}
            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
            title="Edit testimonial"
          >
            <Edit size={18} />
          </button>
          <button
            onClick={() => handleDeleteTestimonial(testimonial.id)}
            className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
            title="Delete testimonial"
          >
            <Trash2 size={18} />
          </button>
        </div>
      </div>
      
      <blockquote className="text-gray-600 italic">
        "{testimonial.content}"
      </blockquote>
      
      <div className="mt-4 flex items-center justify-between text-sm text-gray-500">
        <span>Order: {testimonial.order}</span>
        <span>{testimonial.is_featured ? 'Featured' : 'Regular'}</span>
      </div>
    </div>
  );

  const TestimonialForm = () => (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900">
          {editingTestimonial ? 'Edit Testimonial' : 'Create New Testimonial'}
        </h3>
        <button
          onClick={() => {
            setEditingTestimonial(null);
            setShowCreateForm(false);
            resetForm();
          }}
          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg"
        >
          <X size={18} />
        </button>
      </div>

      <div className="space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Client Name</label>
            <input
              type="text"
              value={formData.client_name}
              onChange={(e) => setFormData(prev => ({...prev, client_name: e.target.value}))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="John Smith"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Position</label>
            <input
              type="text"
              value={formData.position}
              onChange={(e) => setFormData(prev => ({...prev, position: e.target.value}))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="CEO"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Organization</label>
          <input
            type="text"
            value={formData.organization}
            onChange={(e) => setFormData(prev => ({...prev, organization: e.target.value}))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Company Name"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Testimonial Content</label>
          <textarea
            value={formData.content}
            onChange={(e) => setFormData(prev => ({...prev, content: e.target.value}))}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Share the client's experience and feedback..."
          />
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Rating</label>
            <StarRating 
              rating={formData.rating} 
              onChange={(rating) => setFormData(prev => ({...prev, rating}))}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Client Photo URL</label>
            <div className="relative">
              <ImageIcon className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <input
                type="url"
                value={formData.client_image_url}
                onChange={(e) => setFormData(prev => ({...prev, client_image_url: e.target.value}))}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="https://example.com/photo.jpg"
              />
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="is_featured"
              checked={formData.is_featured}
              onChange={(e) => setFormData(prev => ({...prev, is_featured: e.target.checked}))}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor="is_featured" className="text-sm font-medium text-gray-700">
              Featured testimonial
            </label>
          </div>
        </div>

        {/* Preview */}
        <div className="bg-gray-50 p-4 rounded-lg border-2 border-dashed border-gray-200">
          <h4 className="text-sm font-medium text-gray-600 mb-3">Preview</h4>
          <div className="bg-white p-4 rounded-lg">
            <div className="flex items-start space-x-3 mb-3">
              {formData.client_image_url ? (
                <img 
                  src={formData.client_image_url} 
                  alt="Preview"
                  className="w-10 h-10 rounded-full object-cover"
                />
              ) : (
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <User className="w-5 h-5 text-blue-600" />
                </div>
              )}
              <div>
                <div className="font-medium text-gray-900">
                  {formData.client_name || 'Client Name'}
                </div>
                <div className="text-sm text-gray-600">
                  {formData.position || 'Position'} • {formData.organization || 'Organization'}
                </div>
                <StarRating rating={formData.rating} readonly />
              </div>
            </div>
            <blockquote className="text-gray-600 italic text-sm">
              "{formData.content || 'Testimonial content will appear here...'}"
            </blockquote>
          </div>
        </div>

        <div className="flex space-x-3 pt-4">
          <button
            onClick={editingTestimonial ? handleUpdateTestimonial : handleCreateTestimonial}
            className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Save size={18} />
            <span>{editingTestimonial ? 'Update Testimonial' : 'Create Testimonial'}</span>
          </button>
          <button
            onClick={() => {
              setEditingTestimonial(null);
              setShowCreateForm(false);
              resetForm();
            }}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Testimonials Manager</h2>
          <p className="text-gray-600 mt-1">Manage client testimonials and reviews</p>
        </div>
        <button
          onClick={() => {
            setShowCreateForm(true);
            setEditingTestimonial(null);
            resetForm();
          }}
          className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus size={18} />
          <span>Add Testimonial</span>
        </button>
      </div>

      {/* Featured testimonials indicator */}
      {testimonials.some(t => t.is_featured) && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <Award className="w-5 h-5 text-yellow-600" />
            <span className="font-medium text-yellow-800">
              {testimonials.filter(t => t.is_featured).length} featured testimonial(s) will be highlighted on your website
            </span>
          </div>
        </div>
      )}

      {(showCreateForm || editingTestimonial) && <TestimonialForm />}

      <div className="grid gap-6 md:grid-cols-1 lg:grid-cols-2">
        {testimonials.map(testimonial => (
          <TestimonialCard key={testimonial.id} testimonial={testimonial} />
        ))}
      </div>

      {testimonials.length === 0 && !showCreateForm && (
        <div className="text-center py-12">
          <User className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 mb-4">No testimonials found</p>
          <button
            onClick={() => {
              setShowCreateForm(true);
              resetForm();
            }}
            className="text-blue-600 hover:text-blue-800 font-medium"
          >
            Create your first testimonial
          </button>
        </div>
      )}
    </div>
  );
};

export default TestimonialsManager;